// Issue Pool Component
// Main component for viewing and managing the issue pool using FlexibleCRUD

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { ViewModal } from '@/components/common/ViewModal';
import { EditModal } from '@/components/common/EditModal';
import { issueEntity } from '@/config/entities/issues';
import { issueCategoryEntity } from '@/config/entities/issueCategories';
import { issuePoolService } from '@/services/issuePoolService';
import { createCRUDService } from '@/services/genericCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import { IssueCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

interface IssuePoolProps {
  onIssueSelect?: (issue: any) => void;
}

interface IssueCategoryOption {
  id: string;
  name: string;
}

export const IssuePool: React.FC<IssuePoolProps> = ({ onIssueSelect }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const issueCategoryService = createCRUDService(issueCategoryEntity);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingItem, setViewingItem] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Card view state - default to cards
  const [displayViewMode, setDisplayViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('issue-pool-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved : 'cards';
  });
  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [issueCategories, setIssueCategories] = useState<IssueCategoryOption[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('relevance');
  
  // Use generic card view refresh hook
  const { refreshKey, refresh } = useCardViewRefresh(displayViewMode);

  // Delete confirmation dialog state (shared for card and table views)
  const [issueToDelete, setIssueToDelete] = useState<any | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('issue-pool-view-mode', displayViewMode);
  }, [displayViewMode]);

  // Load card data when in card view
  useEffect(() => {
    if (displayViewMode === 'cards') {
      loadCardData();
    }
  }, [displayViewMode, refreshKey, selectedCategoryIds]);

  useEffect(() => {
    const loadIssueCategories = async () => {
      try {
        const response = await issueCategoryService.getAll({ page: 1, limit: 500 });
        const rows = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
        const options = rows
          .filter((row: any) => row?.id && row?.name)
          .map((row: any) => ({ id: String(row.id), name: String(row.name) }));
        setIssueCategories(options);
      } catch (error) {
        console.error('Error loading issue categories:', error);
        setIssueCategories([]);
      }
    };

    loadIssueCategories();
  }, []);

  const loadCardData = async () => {
    try {
      setCardLoading(true);
      const response = await issuePoolService.getAll({
        issue_category_ids: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined
      });
      const issues = response.data || [];
      setCardData(issues);
      applyFiltersAndSort(issues, searchTerm, activeFilters, sortBy);
    } catch (error) {
      console.error('Error loading issues for card view:', error);
    } finally {
      setCardLoading(false);
    }
  };

  // Apply filters and sorting to card data
  const applyFiltersAndSort = (
    data: any[],
    search: string,
    filters: string[],
    sort: string
  ) => {
    let filtered = [...data];

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.category_name?.toLowerCase().includes(searchLower) ||
        item.assigned_to_name?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters: urgency and impact are separate; status as before
    if (filters.length > 0) {
      const urgencySelected = filters.filter(id => id.startsWith('urgency:')).map(id => id.replace('urgency:', '').toUpperCase());
      const impactSelected = filters.filter(id => id.startsWith('impact:')).map(id => id.replace('impact:', '').toUpperCase());
      const statusSelected = filters.filter(id => ['open', 'in-progress', 'resolved', 'closed'].includes(id));

      filtered = filtered.filter(item => {
        if (urgencySelected.length > 0 && !urgencySelected.includes(item.urgency || '')) return false;
        if (impactSelected.length > 0 && !impactSelected.includes(item.impact || '')) return false;
        if (statusSelected.length > 0) {
          const statusMatches = statusSelected.some(s =>
            s === 'in-progress' ? item.status === 'IN_PROGRESS' : (item.status?.toLowerCase() === s)
          );
          if (!statusMatches) return false;
        }
        return true;
      });
    }

    // Apply sorting
    switch (sort) {
      case 'name':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case 'priority-high': {
        const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
        filtered.sort((a, b) => (priorityOrder[b.urgency || ''] || 0) - (priorityOrder[a.urgency || ''] || 0));
        break;
      }
      case 'relevance':
      default:
        // Smart defaults: open first, then by urgency
        filtered.sort((a, b) => {
          const aOpen = a.status === 'OPEN';
          const bOpen = b.status === 'OPEN';
          if (aOpen && !bOpen) return -1;
          if (!aOpen && bOpen) return 1;
          const priorityOrder: Record<string, number> = { 'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
          return (priorityOrder[b.urgency || ''] || 0) - (priorityOrder[a.urgency || ''] || 0);
        });
        break;
    }

    setFilteredCardData(filtered);
  };

  // Update filtered data when search, filters, or sort changes
  useEffect(() => {
    if (displayViewMode === 'cards' && cardData.length > 0) {
      applyFiltersAndSort(cardData, searchTerm, activeFilters, sortBy);
    }
  }, [searchTerm, activeFilters, sortBy, cardData, displayViewMode]);

  const handleView = (issue: any) => {
    if (onIssueSelect) {
      onIssueSelect(issue);
    } else {
      // Open ViewModal instead of navigating to detail page (like RiskPool)
      setViewingItem(issue);
      setShowViewModal(true);
    }
  };

  const handleEdit = (issue: any) => {
    setEditingItem(issue);
    setShowEditModal(true);
  };

  const handleDelete = async (issue: any) => {
    try {
      await issuePoolService.delete(issue.id);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error deleting issue:', error);
      alert(t('issues.deleteError') || 'Failed to delete issue');
      throw error;
    }
  };

  const handleDeleteWithConfirm = (issue: any) => {
    if (skipDeleteConfirmation) {
      handleDelete(issue);
    } else {
      setIssueToDelete(issue);
    }
  };

  const handleConfirmDeleteIssue = async () => {
    if (!issueToDelete) return;
    await handleDelete(issueToDelete);
  };

  const handleCreate = () => {
    navigate('/issues/create');
  };

  const handleEditSubmit = async (data: any) => {
    setLoading(true);
    try {
      await issuePoolService.update(editingItem.id, data);
      setShowEditModal(false);
      setEditingItem(null);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error updating issue:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowEditModal(false);
    setShowViewModal(false);
    setEditingItem(null);
    setViewingItem(null);
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  };

  const statusUrgencyFromActiveFilters = useMemo(() => {
    const statusMap: Record<string, string> = {
      'open': 'OPEN',
      'in-progress': 'IN_PROGRESS',
      'resolved': 'RESOLVED',
      'closed': 'CLOSED'
    };
    const severityMap: Record<string, string> = {
      'critical': 'CRITICAL',
      'high': 'HIGH',
      'medium': 'MEDIUM',
      'low': 'LOW'
    };
    const statuses = activeFilters
      .map(id => statusMap[id])
      .filter(Boolean);
    const urgencyValues = activeFilters
      .filter(id => id.startsWith('urgency:'))
      .map(id => severityMap[id.replace('urgency:', '')])
      .filter(Boolean);
    const impactValues = activeFilters
      .filter(id => id.startsWith('impact:'))
      .map(id => severityMap[id.replace('impact:', '')])
      .filter(Boolean);
    return {
      status: statuses.length > 0 ? statuses.join(',') : undefined,
      urgency: urgencyValues.length > 0 ? urgencyValues.join(',') : undefined,
      impact: impactValues.length > 0 ? impactValues.join(',') : undefined
    };
  }, [activeFilters]);

  const issuePoolServiceWithCategoryFilters = useMemo(() => ({
    ...issuePoolService,
    getAll: (params: any = {}) => issuePoolService.getAll({
      ...params,
      issue_category_ids: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
      ...statusUrgencyFromActiveFilters,
      search: searchTerm.trim() || params.search
    })
  }), [selectedCategoryIds, statusUrgencyFromActiveFilters, searchTerm]);

  const categoryQuickFilters = useMemo(
    () => issueCategories.map(category => ({
      id: `category:${category.id}`,
      label: category.name,
      value: `category:${category.id}`
    })),
    [issueCategories]
  );

  const combinedActiveFilters = useMemo(
    () => [...activeFilters, ...selectedCategoryIds.map(id => `category:${id}`)],
    [activeFilters, selectedCategoryIds]
  );

  const issueQuickFilterGroups = useMemo(() => {
    const severityOptions = [
      { id: 'critical', label: 'Critical', value: 'critical' },
      { id: 'high', label: 'High', value: 'high' },
      { id: 'medium', label: 'Medium', value: 'medium' },
      { id: 'low', label: 'Low', value: 'low' }
    ];
    return [
      {
        id: 'urgency',
        label: t('issues.filters.urgency') || 'Urgency',
        filters: severityOptions.map(opt => ({ ...opt, id: `urgency:${opt.id}`, value: `urgency:${opt.id}` }))
      },
      {
        id: 'impact',
        label: t('issues.filters.impact') || 'Impact',
        filters: severityOptions.map(opt => ({ ...opt, id: `impact:${opt.id}`, value: `impact:${opt.id}` }))
      },
      {
        id: 'status',
        label: t('issues.filters.status') || 'Status',
        filters: [
          { id: 'open', label: 'Open', value: 'open' },
          { id: 'in-progress', label: 'In Progress', value: 'in-progress' },
          { id: 'resolved', label: 'Resolved', value: 'resolved' },
          { id: 'closed', label: 'Closed', value: 'closed' }
        ]
      },
      {
        id: 'issue-categories',
        label: t('issues.filters.categories') || 'Category',
        filters: categoryQuickFilters
      }
    ];
  }, [categoryQuickFilters, t]);

  const getIssueFilterColorClass = (filter: { id: string; label: string }, isActive: boolean): string | undefined => {
    const id = String(filter.id || '').toLowerCase();
    const severityPart = (id.startsWith('urgency:') || id.startsWith('impact:')) ? id.split(':')[1] || id : id;

    const tone = (activeClass: string, inactiveClass: string) => (isActive ? activeClass : inactiveClass);

    const isCategory = id.startsWith('category:');
    const isSeverity = ['critical', 'high', 'medium', 'low'].includes(severityPart);
    const isStatus = ['open', 'in-progress', 'in_progress', 'resolved', 'closed'].includes(id);

    // Severity (urgency/impact): font color only.
    if (isSeverity) {
      if (severityPart === 'critical') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-red-700 hover:bg-red-50'
        );
      }
      if (severityPart === 'high') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-rose-600 hover:bg-rose-50'
        );
      }
      if (severityPart === 'medium') {
        return tone(
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 font-semibold',
          'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-amber-700 hover:bg-amber-50'
        );
      }
      return tone(
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 font-semibold',
        'bg-[var(--color-secondary-bg)] border border-[var(--color-border)] text-emerald-700 hover:bg-emerald-50'
      );
    }

    // Status: border color only.
    if (isStatus) {
      if (id === 'resolved') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-green-600 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-green-400 hover:bg-green-50'
        );
      }
      if (id === 'in-progress' || id === 'in_progress') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-yellow-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-yellow-300 hover:bg-yellow-50'
        );
      }
      if (id === 'open') {
        return tone(
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-blue-500 font-semibold',
          'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-blue-300 hover:bg-blue-50'
        );
      }
      return tone(
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border-2 border-gray-500 font-semibold',
        'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-gray-300 hover:bg-gray-50'
      );
    }

    // Category: no background (match urgency/impact/status — border + text only).
    if (isCategory) {
      return tone(
        'bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] font-semibold',
        'bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] hover:bg-[var(--color-hover-bg)]'
      );
    }

    return undefined;
  };

  return (
    <div className="issue-pool">
      <div className="mb-4">
        <FilterToolbar
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t('issues.searchPlaceholder') || 'Search issues...'}
          quickFilterGroups={issueQuickFilterGroups}
          activeFilters={combinedActiveFilters}
          onFilterToggle={(filterId) => {
            if (filterId.startsWith('category:')) {
              const categoryId = filterId.replace('category:', '');
              if (categoryId) {
                toggleCategory(categoryId);
              }
              return;
            }
            setActiveFilters(prev =>
              prev.includes(filterId)
                ? prev.filter(id => id !== filterId)
                : [...prev, filterId]
            );
          }}
          sortOptions={[
            { id: 'relevance', label: 'Relevance', value: 'relevance' },
            { id: 'name', label: 'Title (A-Z)', value: 'name' },
            { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
            { id: 'priority-high', label: 'Urgency (High)', value: 'priority-high' }
          ]}
          sortValue={sortBy}
          onSortChange={setSortBy}
          filterColorResolver={getIssueFilterColorClass}
          controlsAfterSort={
            <ViewSwitcher
              currentView={displayViewMode}
              onViewChange={setDisplayViewMode}
            />
          }
        />
      </div>

      {displayViewMode === 'table' ? (
        <FlexibleCRUDTable
          key={`${refreshKey}:${selectedCategoryIds.join(',')}:${activeFilters.join(',')}:${searchTerm}`}
          entity={issueEntity}
          service={issuePoolServiceWithCategoryFilters}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          useConfirmDialog={true}
          skipDeleteConfirmation={skipDeleteConfirmation}
          onDeleteDontAskAgain={() => setSkipDeleteConfirmation(true)}
          onCreate={handleCreate}
          showCreateButton={false}
          onOperationComplete={() => {
            refresh(); // Use generic refresh hook
          }}
          searchPlaceholder={t('issues.searchPlaceholder') || 'Search issues...'}
          showPagination={true}
          showSearch={false}
        />
      ) : (
        <div>
          {cardLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
            </div>
          ) : filteredCardData.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                {cardData.length === 0 
                  ? 'No issues found' 
                  : 'No issues match your filters'}
              </p>
            </div>
          ) : (
            <CardGrid columns={4}>
              {filteredCardData.map((issue) => (
                <IssueCard
                  key={issue.id}
                  issue={issue}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDeleteWithConfirm}
                />
              ))}
            </CardGrid>
          )}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <EditModal
          isOpen={showEditModal}
          onClose={handleCancel}
          title={t('issues.editTitle') || `Edit ${issueEntity.displayNameSingular}`}
          entity={issueEntity}
          initialData={editingItem}
          onSubmit={handleEditSubmit}
          onCancel={handleCancel}
          loading={loading}
        />
      )}

      <ConfirmDialog
        isOpen={!!issueToDelete}
        onClose={() => setIssueToDelete(null)}
        onConfirm={handleConfirmDeleteIssue}
        title={t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'}
        itemName={issueToDelete?.title}
        description={t('messages.deleteWarning') || 'This action cannot be undone.'}
        confirmLabel={t('common.delete') || 'Delete'}
        cancelLabel={t('common.cancel') || 'Cancel'}
        variant="danger"
        showDontAskAgain={true}
        dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
        onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
      />

      {/* View Modal */}
      {showViewModal && viewingItem && (
        <ViewModal
          isOpen={showViewModal}
          onClose={handleCancel}
          title={t('issues.viewTitle') || `View ${issueEntity.displayNameSingular}`}
          data={viewingItem}
          fields={issueEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}
    </div>
  );
};

